/**
 * Which tool connections serve a teammate (#1746, #1758): the company's own, one on a team they are on, or their own.
 * One rule, shared by Add a teammate's "still to connect" check and the Tools field on a teammate's card on Members,
 * so the two can never disagree about what a teammate already has. A tool is the same tool in any case: `Slack` bound
 * for the company and `slack` bound on a team are one tool, Slack.
 */

/**
 * What a teammate's card says about their tools: still being read, unreadable (the read was refused or failed, which
 * leaves every other field of the card as it is), or the plain labels of the tools that serve them.
 */
export type TeammateTools = { status: 'loading' } | { status: 'failed' } | { status: 'ready'; labels: string[] }

/** A connection, as far as the serving rule reads it. */
export type ServingBinding = { toolType: string; layer: string; layerId: string }

/** Whether this connection serves the teammate: the company's own, one on a team they are on, or their own. */
export function servesTeammate(binding: ServingBinding, memberId: string, teamIds: readonly string[]): boolean {
    return (
        binding.layer === 'enterprise' ||
        (binding.layer === 'team' && teamIds.includes(binding.layerId)) ||
        (binding.layer === 'member' && binding.layerId === memberId)
    )
}

/** The key a tool is told apart by: its type, in any case. */
export function toolKey(toolType: string): string {
    return toolType.trim().toLowerCase()
}

/**
 * The tools that serve the teammate, one per tool type in any case: the first connection of each type, in the order
 * the connections are given.
 */
export function toolsServing<T extends ServingBinding>(
    bindings: readonly T[],
    memberId: string,
    teamIds: readonly string[],
): T[] {
    const seen = new Set<string>()
    return bindings.filter(binding => {
        if (!servesTeammate(binding, memberId, teamIds)) return false
        const key = toolKey(binding.toolType)
        if (!key || seen.has(key)) return false
        seen.add(key)
        return true
    })
}
